import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { primaryNavigationItems } from "@/components/ui/navigation-menu";
import { getLocalizedRoute } from "@/lib/routes";
import { isPrerender } from "@/lib/prerender";
import { SITE_CONFIG } from "@/config/site";
import { SERVICE_CATALOG } from "@/data/serviceCatalog";

/**
 * Page-to-page transition. A click on an internal link is held back while an
 * ink panel rises over the page, carrying the destination's name and the
 * logo's byte decoding; the next page's code loads meanwhile. Then the route
 * changes under the panel, which carries on upward and off the screen.
 *
 * Left alone: modified clicks, new tabs, downloads, external links, links to
 * the page already shown (anchors), and anything when reduced motion is
 * preferred. The language switch calls navigateWithTransition itself. Back and forward
 * stay native and instant.
 */
type Phase = "idle" | "cover" | "hold" | "reveal";

const COVER_MS = 560;
const REVEAL_MS = 700;
const MAX_WAIT_MS = 2000;
const BYTE = "10100010";

type GoOptions = { replace?: boolean; label?: string };
let runTransition: ((to: string, opts?: GoOptions) => void) | null = null;

/**
 * Navigate with the panel transition from anywhere (the language switch uses
 * it). Returns false when transitions are off (reduced motion, prerender), so
 * the caller can navigate plainly instead.
 */
export function navigateWithTransition(to: string, opts?: GoOptions): boolean {
  if (!runTransition) return false;
  runTransition(to, opts);
  return true;
}

type RouteTransitionProps = {
  /** Loads the code for a pathname (App's preloadRoute). */
  preload: (pathname: string) => Promise<unknown>;
};

const RouteTransition = ({ preload }: RouteTransitionProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useLanguage();
  const [phase, setPhase] = useState<Phase>("idle");
  const [label, setLabel] = useState("");
  const [bits, setBits] = useState(BYTE);
  const [settled, setSettled] = useState(BYTE.length);
  const busy = useRef(false);

  // Resolve a destination's display name: a main page, a service, or nothing.
  const labelFor = (pathname: string) => {
    if (pathname === getLocalizedRoute("/", language)) return SITE_CONFIG.name;
    const page = primaryNavigationItems.find(
      (item) => item.key !== "home" && getLocalizedRoute(item.href, language) === pathname
    );
    if (page) return t(page.translationKey);
    const service = SERVICE_CATALOG.find(
      (s) => getLocalizedRoute(`${SITE_CONFIG.routes.services}/${s.id}`, language) === pathname
    );
    if (service) return t(`serviceOptions.${service.key}`);
    if (pathname.startsWith(getLocalizedRoute(SITE_CONFIG.routes.articles, language))) return t("nav.articles");
    return "";
  };
  const labelRef = useRef(labelFor);
  useEffect(() => {
    labelRef.current = labelFor;
  });

  useEffect(() => {
    if (isPrerender() || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const go = (to: string, opts: GoOptions = {}) => {
      if (busy.current) return;
      busy.current = true;
      const pathname = new URL(to, window.location.href).pathname;
      setLabel(opts.label ?? labelRef.current(pathname));
      setSettled(0);
      setPhase("cover");

      // Change the route once the panel is up and the page's code is in.
      const covered = new Promise((r) => setTimeout(r, COVER_MS));
      const loaded = Promise.race([preload(pathname).catch(() => undefined), new Promise((r) => setTimeout(r, MAX_WAIT_MS))]);
      Promise.all([covered, loaded]).then(() => {
        navigate(to, { replace: opts.replace });
        setPhase("hold");
      });
    };
    runTransition = go;

    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.("a");
      if (!a || !a.href) return;
      // The language switch drives its own transition (navigateWithTransition).
      if ((a.target && a.target !== "_self") || a.hasAttribute("download") || a.hasAttribute("hreflang")) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname) return;

      e.preventDefault();
      go(url.pathname + url.search + url.hash);
    };

    // Capture phase, so this runs before React Router's own Link handler,
    // which skips navigating when the event has been prevented.
    document.addEventListener("click", onClick, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      if (runTransition === go) runTransition = null;
    };
  }, [navigate, preload]);

  // The new page has rendered under the panel: give it two frames, then lift.
  useEffect(() => {
    if (phase !== "hold") return;
    let timer = 0;
    const frame = requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        setPhase("reveal");
        timer = window.setTimeout(() => {
          setPhase("idle");
          busy.current = false;
        }, REVEAL_MS);
      })
    );
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [phase, location.key]);

  // The byte flickers while the panel rises and settles, left to right, into
  // the logo's 10100010 once it is up.
  useEffect(() => {
    if (phase !== "cover") return;
    const started = performance.now();
    const id = window.setInterval(() => {
      const locked = Math.min(BYTE.length, Math.floor(((performance.now() - started) / COVER_MS) * BYTE.length));
      setSettled(locked);
      setBits(
        [...BYTE].map((b, i) => (i < locked ? b : Math.random() < 0.5 ? "0" : "1")).join("")
      );
    }, 50);
    return () => window.clearInterval(id);
  }, [phase]);

  const clip =
    phase === "idle" ? "inset(100% 0 0 0)" : phase === "reveal" ? "inset(0 0 100% 0)" : "inset(0 0 0 0)";
  const duration = phase === "reveal" ? REVEAL_MS : COVER_MS;

  return (
    <div
      aria-hidden="true"
      className={`fixed inset-0 z-[90] flex flex-col justify-end bg-ink p-5 text-white md:p-8 ${
        phase === "idle" ? "pointer-events-none invisible" : "pointer-events-auto visible"
      }`}
      style={{
        clipPath: clip,
        transition: phase === "idle" ? "none" : `clip-path ${duration}ms cubic-bezier(0.76, 0, 0.24, 1)`,
      }}
    >
      <div
        className="transition-[transform,opacity] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
        style={{
          transform: phase === "cover" || phase === "hold" ? "translate3d(0,0,0)" : "translate3d(0,40px,0)",
          opacity: phase === "reveal" ? 0 : 1,
        }}
      >
        {/* The byte strip from the logo leads; the destination's name follows it. */}
        <div className="flex gap-[clamp(0.3rem,0.9vw,0.8rem)]">
          {[...(phase === "cover" ? bits : BYTE)].map((b, i) => {
            const locked = phase !== "cover" || i < settled;
            return (
              <span
                key={i}
                className={`flex aspect-[5/6] w-[clamp(2.4rem,9.5vw,7.25rem)] items-center justify-center rounded-[clamp(4px,0.45vw,8px)] border-[clamp(2px,0.28vw,4px)] text-[clamp(1.6rem,6.4vw,4.9rem)] font-black leading-none tabular-nums transition-[color,border-color,transform] duration-200 ease-out ${
                  locked ? "scale-100 border-white text-white" : "scale-[0.92] border-white/20 text-white/30"
                }`}
              >
                {b}
              </span>
            );
          })}
        </div>
        {label && <p className="display mt-[clamp(1.1rem,2.6vw,2.25rem)] text-[clamp(1.9rem,4.6vw,4.5rem)]">{label}</p>}
        <div className="mt-[clamp(1.25rem,2.6vw,2rem)] h-px bg-white/15" />
      </div>
    </div>
  );
};

export default RouteTransition;
